import { useEffect, useState } from "react";
import { Menu, Phone, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PHONE, NAV_ITEMS, SITE } from "@/lib/siteConfig";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#071A2B]/95 backdrop-blur shadow-[0_8px_24px_rgba(2,6,23,0.15)] border-b border-white/10"
          : "bg-[#071A2B]/70 backdrop-blur border-b border-white/5"
      }`}
    >
      <div className="ss-container flex h-16 items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => handleAnchor("hero")}
          className="flex items-center gap-2 text-white group"
          data-testid="header-logo"
          aria-label={SITE.brand}
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--ss-gold)] text-[var(--ss-navy)] shadow-[0_6px_16px_rgba(245,197,66,0.35)]">
            <ShieldCheck className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <div className="flex flex-col leading-tight text-left">
            <span className="font-display font-bold text-base tracking-tight">SecurityStaging<span className="text-[var(--ss-gold)]">.de</span></span>
            <span className="text-[10px] uppercase tracking-widest text-slate-400 hidden sm:block">Alarmanlagen-Staging</span>
          </div>
        </button>

        {/* Desktop Nav */}
        <nav
          className="hidden lg:flex items-center gap-1"
          aria-label="Hauptnavigation"
          data-testid="header-nav"
        >
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => handleAnchor(item.id)}
              data-testid={`nav-${item.id}`}
              className="px-3 py-2 text-sm font-medium text-slate-200 hover:text-white transition-colors relative group"
            >
              {item.label}
              <span className="absolute left-3 right-3 -bottom-0.5 h-[2px] bg-[var(--ss-gold)] scale-x-0 group-hover:scale-x-100 origin-left transition-transform" />
            </button>
          ))}
        </nav>

        {/* Phone CTA */}
        <div className="flex items-center gap-2">
          <a
            href={`tel:${PHONE.tel}`}
            className="ss-btn-primary hidden sm:inline-flex"
            data-testid="header-phone-cta"
          >
            <Phone className="h-4 w-4" strokeWidth={2.5} />
            <span className="whitespace-nowrap">{PHONE.display}</span>
          </a>
          <a
            href={`tel:${PHONE.tel}`}
            className="inline-flex sm:hidden ss-btn-primary !px-3"
            data-testid="header-phone-cta-mobile"
            aria-label="Jetzt anrufen"
          >
            <Phone className="h-4 w-4" strokeWidth={2.5} />
            <span>Anrufen</span>
          </a>

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/15 text-white hover:bg-white/10 transition"
                aria-label="Menü öffnen"
                data-testid="mobile-menu-button"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#071A2B] text-white border-l border-white/10">
              <SheetHeader>
                <SheetTitle className="text-white font-display">Menü</SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-1" aria-label="Mobile Navigation">
                {NAV_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleAnchor(item.id)}
                    className="text-left px-4 py-3 rounded-lg hover:bg-white/5 text-slate-200 hover:text-white transition"
                    data-testid={`mobile-nav-${item.id}`}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
              <div className="mt-6">
                <a
                  href={`tel:${PHONE.tel}`}
                  className="ss-btn-primary w-full"
                  data-testid="mobile-phone-cta"
                >
                  <Phone className="h-4 w-4" />
                  {PHONE.display}
                </a>
                <p className="text-xs text-slate-400 mt-3 text-center">{PHONE.hours}</p>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
